import { type IThemePalette } from "../types";
import { BrandColors } from "../base";
import { type IButtonTheme, getButtonTheme } from "./button-theme";
import { type IPopupMenuTheme, getPopupMenuTheme } from "./popup-menu-theme";
import { type ISelectorTheme, getSelectorTheme } from "./selector-theme";
import { type IChipTheme, getChipTheme } from "./chip-theme";
import { type ICardTheme, getCardTheme } from "./card-theme";
import { type ISymbolTheme, getSymbolTheme } from "./symbol-theme";
import { type IInputFormTheme, getInputFormTheme } from "./input-form-theme";
import { type IProgressBarTheme, getProgressBarTheme } from "./progress-bar-theme";

export * from "./button-theme";
export * from "./popup-menu-theme";
export * from "./selector-theme";
export * from "./chip-theme";
export * from "./card-theme";
export * from "./symbol-theme";
export * from "./input-form-theme";
export * from "./progress-bar-theme";

/**
 * Geometric control heights standardizing interactive targets across the platform.
 */
export const ControlSizes = {
  xs: 22,
  sm: 26,
  compact: 30,
  md: 36,
  lg: 42,
} as const;

/**
 * Corner radii scale enforcing consistent curvature across controls and cards.
 */
export const BorderRadii = {
  none: 0,
  xs: 3,
  sm: 4,
  md: 6,
  lg: 8,
  full: 9999,
} as const;

/**
 * Layering z-indices standardizing popups, dropdowns, and sticky headers.
 */
export const ZIndex = {
  base: 1,
  sticky: 20,
  dropdown: 50,
  popover: 9999,
  modalCard: 10000,
} as const;

/**
 * Deterministic interactive border resolver state machine.
 * Guarantees consistent border transitions across all controls without layout shifts.
 */
export function resolveInteractiveBorder({
  palette,
  readOnly,
  disabled,
  hasError,
  isSelected,
  isOpen,
  isFocused,
  isHovered,
  restingBorder,
  hoverBorder,
}: {
  palette: IThemePalette;
  readOnly?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  isSelected?: boolean;
  isOpen?: boolean;
  isFocused?: boolean;
  isHovered?: boolean;
  restingBorder?: string;
  hoverBorder?: string;
}): string {
  if (readOnly || disabled) {
    return palette.inputReadOnlyBorder;
  }
  if (hasError) {
    return BrandColors.beetRed.hex;
  }
  if (isSelected || isOpen || isFocused) {
    return palette.tint;
  }
  if (isHovered) {
    return hoverBorder || palette.buttonHoverBorder;
  }
  return restingBorder || palette.border;
}

/**
 * Aggregated bundle containing all 7 Layer 2 component theme recipes.
 */
export interface IComponentThemes {
  palette: IThemePalette;
  isDark: boolean;
  button: IButtonTheme;
  popupMenu: IPopupMenuTheme;
  selector: ISelectorTheme;
  chip: IChipTheme;
  card: ICardTheme;
  symbol: ISymbolTheme;
  inputForm: IInputFormTheme;
  progressBar: IProgressBarTheme;
}

/**
 * Pure factory generating the complete IComponentThemes bundle for an active palette.
 */
export function getComponentThemes(palette: IThemePalette, isDark: boolean): IComponentThemes {
  return {
    palette,
    isDark,
    button: getButtonTheme(palette, isDark),
    popupMenu: getPopupMenuTheme(palette, isDark),
    selector: getSelectorTheme(palette, isDark),
    chip: getChipTheme(palette, isDark),
    card: getCardTheme(palette, isDark),
    symbol: getSymbolTheme(palette, isDark),
    inputForm: getInputFormTheme(palette, isDark),
    progressBar: getProgressBarTheme(palette, isDark),
  };
}
